/**
 * Canonical notification screen targets for push campaigns (admin UI).
 * Stored `value` is sent as `intentType`; `label` is shown in the UI only.
 */
export type NotificationScreenAudience = 'user' | 'doctor' | 'ambulance' | 'delivery_partner' | 'vendor';

export interface NotificationScreenOption {
  value: string;
  label: string;
  audience: NotificationScreenAudience;
}

/** Allowed screen types (only these in the create/edit form for now). */
export const NOTIFICATION_SCREEN_TYPES: NotificationScreenOption[] = [
  // --- User Screens ---
  { value: 'USER_HOME', label: 'User home screen', audience: 'user' },
  { value: 'USER_ORDER', label: 'User Order screen', audience: 'user' },
  { value: 'HEALTH_RECORDS', label: 'Health records', audience: 'user' },
  { value: 'USER_PROFILE', label: 'User profile', audience: 'user' },
  { value: 'USER_FEEDBACK', label: 'Feedback', audience: 'user' },
  { value: 'USER_LOGIN', label: 'Login screen', audience: 'user' },
  { value: 'EXTERNAL_LINK', label: 'External Link / Video URL', audience: 'user' },

  // --- Doctor / Clinic Screens ---
  { value: 'DOCTOR_LOGIN', label: 'Login screen', audience: 'doctor' },
  { value: 'DOCTOR_DASHBOARD', label: 'Doctor Dashboard', audience: 'doctor' },
  { value: 'CLINIC_APPOINTMENT', label: 'Appointments List', audience: 'doctor' },
  { value: 'CLINIC_APPOINTMENT_CANCELLED', label: 'Appointment Cancelled', audience: 'doctor' },
  { value: 'CLINIC_APPOINTMENT_REMINDER', label: 'Appointment Reminder', audience: 'doctor' },
  { value: 'CLINIC_APPOINTMENT_HISTORY', label: 'Appointment History', audience: 'doctor' },
  { value: 'CLINIC_APPOINTMENT_COMPLETED', label: 'Appointment Completed', audience: 'doctor' },
  { value: 'CLINIC_TIMESLOT_UPDATED', label: 'Timeslot Updated', audience: 'doctor' },
  { value: 'CLINIC_TIMESLOT_DISABLED', label: 'Timeslot Disabled', audience: 'doctor' },
  { value: 'CLINIC_TIMESLOT_ENABLED', label: 'Timeslot Enabled', audience: 'doctor' },
  { value: 'INSTANT_CONSULTATION', label: 'Instant Consultation', audience: 'doctor' },
  { value: 'INSTANT_CONSULTATION_REMINDER', label: 'Instant Consultation Reminder', audience: 'doctor' },
  { value: 'INSTANT_CONSULTATION_PAYMENT_COMPLETED', label: 'Instant Consultation Payment Completed', audience: 'doctor' },
  { value: 'CLINIC_CHAT_MESSAGE', label: 'Patient Chat', audience: 'doctor' },
  { value: 'CLINIC_MEETING_EXTEND_REQUEST', label: 'Meeting Extend Request', audience: 'doctor' },
  { value: 'CLINIC_MEETING_EXTEND_REJECTED', label: 'Meeting Extend Rejected', audience: 'doctor' },
  { value: 'CLINIC_NEXT_APPOINTMENT_RESCHEDULED', label: 'Next Appointment Rescheduled', audience: 'doctor' },

  // --- Ambulance Agency Screens ---
  { value: 'AMBULANCE_LOGIN', label: 'Login screen', audience: 'ambulance' },
  { value: 'AMBULANCE_AGENCY_DASHBOARD', label: 'Ambulance Agency Dashboard', audience: 'ambulance' },
  { value: 'NEW_BOOKING', label: 'New Booking', audience: 'ambulance' },
  { value: 'ambulance_NEW_BOOKING', label: 'Ambulance New Booking (Legacy)', audience: 'ambulance' },
  { value: 'ambulance_HELP_REQUEST', label: 'Ambulance Help Request', audience: 'ambulance' },
  { value: 'ambulance_AmbulancePage', label: 'Ambulance Page', audience: 'ambulance' },
  { value: 'ambulance_OrderHistory', label: 'Ambulance Order History', audience: 'ambulance' },
  { value: 'ambulance_BOOKING_CANCELLED', label: 'Ambulance Booking Cancelled', audience: 'ambulance' },
  { value: 'AMBULANCE_SETTLEMENT', label: 'Ambulance Settlement', audience: 'ambulance' },

  // --- Ambulance Driver / Delivery Partner Screens ---
  { value: 'AMBULANCE_DRIVER_DASHBOARD', label: 'Ambulance Driver Dashboard', audience: 'delivery_partner' },
  { value: 'AMBULANCE_DRIVER_BOOKING_ASSIGNED', label: 'Booking Assigned', audience: 'delivery_partner' },
  { value: 'AMBULANCE_DRIVER_BOOKING_REASSIGNED', label: 'Booking Reassigned', audience: 'delivery_partner' },
  { value: 'AMBULANCE_DRIVER_REQUESTS', label: 'Driver Requests', audience: 'delivery_partner' },

  // --- Other General Vendor Screens ---
  { value: 'VENDOR_LOGIN', label: 'Login screen', audience: 'vendor' },
  { value: 'MEDICAL_STORE_DASHBOARD', label: 'Medical Store Dashboard', audience: 'vendor' },
  { value: 'HOSPITAL_DASHBOARD', label: 'Hospital Dashboard', audience: 'vendor' },
  { value: 'PATHOLOGY_DASHBOARD', label: 'Pathology Dashboard', audience: 'vendor' },
  { value: 'BLOOD_BANK_DASHBOARD', label: 'Blood Bank Dashboard', audience: 'vendor' },
  { value: 'PRODUCT_PARTNER_DASHBOARD', label: 'Product Partner Dashboard', audience: 'vendor' },
];

const labelByValue = new Map<string, string>(
  NOTIFICATION_SCREEN_TYPES.map((o) => [o.value, o.label])
);

/** Human-readable label for a stored intent type key. */
export function getNotificationScreenLabel(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  return labelByValue.get(value) ?? value;
}

export type TargetAudienceFilter = 'ALL_USERS' | 'VENDORS' | 'DELIVERY_PARTNERS' | 'UNREGISTERED_GUESTS' | 'SINGLE_USER' | 'INACTIVE_USERS' | 'USERS_WITHOUT_HEALTH_RECORD' | 'LOGGED_OUT_USERS' | 'DOCTORS' | 'AMBULANCE' | 'INACTIVE_VENDORS' | 'LOGGED_OUT_VENDORS' | 'PENDING_APPROVAL_VENDORS' | 'INACTIVE_DOCTORS' | 'PENDING_APPROVAL_DOCTORS' | 'INACTIVE_AMBULANCE' | 'PENDING_APPROVAL_AMBULANCE' | 'LOGGED_OUT_DOCTORS' | 'LOGGED_OUT_AMBULANCE' | 'SINGLE_VENDOR' | 'SINGLE_DOCTOR' | 'SINGLE_AMBULANCE';

/** Filter options based on selected target audience. */
export function getScreenTypeOptionsForAudience(audience: TargetAudienceFilter): NotificationScreenOption[] {
  if (
    audience === 'DOCTORS' ||
    audience === 'INACTIVE_DOCTORS' ||
    audience === 'PENDING_APPROVAL_DOCTORS' ||
    audience === 'LOGGED_OUT_DOCTORS' ||
    audience === 'SINGLE_DOCTOR'
  ) {
    return NOTIFICATION_SCREEN_TYPES.filter((o) => o.audience === 'doctor');
  }
  if (
    audience === 'AMBULANCE' ||
    audience === 'INACTIVE_AMBULANCE' ||
    audience === 'PENDING_APPROVAL_AMBULANCE' ||
    audience === 'LOGGED_OUT_AMBULANCE' ||
    audience === 'SINGLE_AMBULANCE'
  ) {
    return NOTIFICATION_SCREEN_TYPES.filter((o) => o.audience === 'ambulance');
  }
  if (audience === 'DELIVERY_PARTNERS') {
    return NOTIFICATION_SCREEN_TYPES.filter((o) => o.audience === 'delivery_partner');
  }
  if (
    audience === 'VENDORS' ||
    audience === 'INACTIVE_VENDORS' ||
    audience === 'LOGGED_OUT_VENDORS' ||
    audience === 'PENDING_APPROVAL_VENDORS' ||
    audience === 'SINGLE_VENDOR'
  ) {
    // Under general vendor filters, show general vendor dashboards + ambulance + doctor screens
    return NOTIFICATION_SCREEN_TYPES.filter((o) => o.audience === 'vendor' || o.audience === 'doctor' || o.audience === 'ambulance');
  }
  // Default to user screens for user audiences
  return NOTIFICATION_SCREEN_TYPES.filter((o) => o.audience === 'user');
}

export function getDefaultScreenTypeForAudience(audience: TargetAudienceFilter): string {
  const options = getScreenTypeOptionsForAudience(audience);
  return options[0]?.value ?? 'USER_HOME';
}

/** Hint for optional intent payload. */
export function getIntentValueHint(screenType: string): string {
  if (screenType === 'EXTERNAL_LINK') {
    return 'Required: Enter the full YouTube or Web URL (e.g., https://youtube.com/watch?v=...) to open on tap.';
  }
  if (screenType === 'USER_ORDER') {
    return 'Optional: order id or JSON such as {"orderId":"..."} if the app needs it.';
  }
  if (screenType === 'USER_FEEDBACK' || screenType === 'FeedbackPrompt') {
    return 'Optional JSON: {"feedbackPromptId":"...","serviceType":"..."} for deep links.';
  }
  return 'Optional extra data (plain text or JSON) included in the notification payload.';
}
